/*
 * FLO-COV-01 (site-wide, 2026-10-08) — muted autoplay for videos on every page (PDPs, pages, landing pages).
 *
 * What it does, for each video on the page (including ones added later):
 *  - forces muted + inline playback (required for autoplay on iOS, Android and in-app browsers such as Instagram),
 *  - plays the video silently on a loop when it scrolls into view and pauses it when it leaves,
 *  - if the browser blocks playback (e.g. iPhone Low Power Mode), it shows the browser's play controls so the
 *    visitor can start the video with a tap,
 *  - YouTube / Vimeo embeds are switched to muted autoplay when they scroll into view,
 *  - click-to-load players (Dawn <deferred-media>, Prestige <video-media>) are loaded and played when in view.
 *
 * Left alone (never touched):
 *  - review sections and review apps (Okendo, Loox, Judge.me, Yotpo, Stamped, Tolstoy, "Video reviews" cards,
 *    testimonial sections — matched by class / id words such as "review", "testimonial"),
 *  - product image galleries on product pages (product videos there stay tap-to-play),
 *  - pop-ups / modals / lightboxes,
 *  - videos already handled by the landing-page autoplay scripts (data-dpl006-lazy-video, data-lp-autoplay-video,
 *    data-flo-autoplay-video),
 *  - anything inside an element with data-autoplay-skip, or a video with data-no-autoplay.
 *
 * To switch the whole feature off, remove the one <script> line that loads this file from the layout(s).
 */
(() => {
  if (window.SiteVideoAutoplay) {
    window.SiteVideoAutoplay.scan(document);
    return;
  }

  const SKIP_SELF = '[data-dpl006-lazy-video],[data-lp-autoplay-video],[data-flo-autoplay-video],[data-no-autoplay]';
  /* Explicit containers that are never touched. */
  const SKIP_AREA = [
    '[data-autoplay-skip]',
    '[data-oke-widget]', '[data-oke-reviews-product-id]', '.okeReviews', '#looxReviews', '.jdgm-widget',
    'media-gallery', 'product-gallery', 'product-modal', 'deferred-media.product__modal-opener',
    'dialog', '[role="dialog"]', 'modal-dialog', 'details-modal'
  ].join(',');
  /* Class / id words that mark review, testimonial, gallery or pop-up areas. Matched as whole words inside
     class names and ids (e.g. "video-reviews-section", "template--1__video_reviews_x"), so "preview" or
     "modalities" do not match. */
  const SKIP_WORDS = ['review', 'reviews', 'testimonial', 'testimonials', 'ugc', 'loox', 'okendo', 'oke', 'jdgm',
    'judgeme', 'yotpo', 'stamped', 'tolstoy', 'modal', 'popup', 'lightbox'];
  const GALLERY_PREFIX = ['product__media', 'product-gallery', 'product-media-modal', 'product__modal'];
  const wordsOf = (el) => {
    const raw = ((typeof el.className === 'string' ? el.className : (el.getAttribute && el.getAttribute('class')) || '') + ' ' + (el.id || '')).toLowerCase();
    return raw.split(/\s+/).filter(Boolean);
  };
  /* Last tap / click / key press by the visitor, used to tell a visitor's pause from a script's pause. */
  let lastGesture = { time: 0, target: null };
  ['pointerdown', 'touchstart', 'keydown', 'click'].forEach((type) => {
    document.addEventListener(type, (event) => {
      lastGesture = { time: Date.now(), target: event.target };
    }, { capture: true, passive: true });
  });
  const gestureNear = (video) => {
    if (Date.now() - lastGesture.time > 1500 || !lastGesture.target) return false;
    const target = lastGesture.target;
    const area = video.parentElement || video;
    return target === video || (target.nodeType === 1 && (area.contains(target) || target.contains(video)));
  };

  const registered = new WeakSet();
  const visible = new WeakMap();
  const userPaused = new WeakSet();
  const ourPause = new WeakSet();
  let observer = null;

  const skipped = (el) => {
    try {
      for (let node = el; node && node.nodeType === 1 && node !== document.body && node !== document.documentElement; node = node.parentElement) {
        if (node.matches(SKIP_AREA)) return true;
        const tokens = wordsOf(node);
        for (let i = 0; i < tokens.length; i++) {
          const token = tokens[i];
          if (GALLERY_PREFIX.some((prefix) => token.indexOf(prefix) === 0)) return true;
          const parts = token.split(/[-_]+/);
          if (parts.some((part) => SKIP_WORDS.indexOf(part) !== -1)) return true;
        }
      }
      return false;
    } catch (e) {
      return true;
    }
  };

  /* Existing play-button overlays next to a video: hidden while the video plays, shown again if it is blocked. */
  const OVERLAY = '.custom-play-button,[data-flo-play-video],.deferred-media__poster-button,[class*="play-button"],[class*="play_button"],[class*="play-btn"],[class*="video-play"],[class*="play-icon"]';
  const overlaysFor = (video) => {
    const parent = video.parentElement;
    if (!parent) return [];
    return Array.from(parent.children).filter((child) => child !== video && child.tagName !== 'A' && child.matches(OVERLAY));
  };
  const setOverlays = (video, show) => {
    overlaysFor(video).forEach((button) => {
      if (show) {
        if (button.dataset.autoplayHidden !== undefined) {
          button.style.display = button.dataset.autoplayHidden;
          delete button.dataset.autoplayHidden;
        }
      } else if (button.dataset.autoplayHidden === undefined) {
        button.dataset.autoplayHidden = button.style.display || '';
        button.style.display = 'none';
      }
    });
  };

  /* ---------- <video> elements ---------- */
  const prepare = (video) => {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    if (!video.hasAttribute('data-autoplay-no-loop')) video.loop = true;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      if (!source.getAttribute('src')) {
        source.setAttribute('src', source.dataset.src || '');
        video.load();
      }
    });
    if (!video.getAttribute('src') && video.dataset.src && !video.querySelector('source')) {
      video.setAttribute('src', video.dataset.src);
    }
    if (video.getAttribute('preload') === 'none') video.setAttribute('preload', 'metadata');
  };

  const showFallback = (video) => {
    video.dataset.autoplayState = 'blocked';
    setOverlays(video, true);
    if (overlaysFor(video).length) return; /* the section's own play button is the fallback */
    if (!video.controls) {
      video.controls = true;
      video.dataset.autoplayAddedControls = '';
    }
  };

  const tryPlay = (video) => {
    if (!visible.get(video) || userPaused.has(video)) return;
    if (!video.paused) return;
    prepare(video);
    const attempt = video.play();
    if (attempt && attempt.catch) {
      attempt.then(() => {
        video.dataset.autoplayState = 'playing';
      }).catch((error) => {
        if (error && error.name === 'AbortError') return;
        showFallback(video);
      });
    }
  };

  const registerVideo = (video) => {
    if (registered.has(video)) return;
    if (video.matches(SKIP_SELF) || skipped(video)) return;
    if (video.closest('video-media')) return; /* Prestige player: handled through its own play()/pause() below */
    registered.add(video);
    visible.set(video, false);
    video.dataset.autoplayState = 'idle';
    video.addEventListener('pause', () => {
      if (ourPause.has(video)) {
        ourPause.delete(video);
        return;
      }
      /* Only a pause that follows the visitor's own tap / click / key press on (or next to) this video counts
         as "the visitor paused it". Pauses made by the page's own scripts (e.g. a strip that plays one video
         at a time) are not fought and not remembered: the video is tried again when it next scrolls into view. */
      if (visible.get(video) && !video.ended && !document.hidden && gestureNear(video)) userPaused.add(video);
    });
    video.addEventListener('playing', () => {
      video.dataset.autoplayState = 'playing';
      setOverlays(video, false);
      if (video.dataset.autoplayAddedControls !== undefined) {
        video.controls = false;
        delete video.dataset.autoplayAddedControls;
      }
    });
    video.addEventListener('canplay', () => tryPlay(video));
    if (observer) observer.observe(video);
    else {
      visible.set(video, true);
      tryPlay(video);
    }
  };

  /* ---------- YouTube / Vimeo iframes ---------- */
  const EMBED = /(youtube\.com\/embed|youtube-nocookie\.com\/embed|player\.vimeo\.com\/video)/i;
  const autoplaySrc = (src) => {
    try {
      const u = new URL(src, window.location.href);
      if (/vimeo/i.test(u.hostname)) {
        u.searchParams.set('autoplay', '1');
        u.searchParams.set('muted', '1');
        u.searchParams.set('playsinline', '1');
        u.searchParams.set('loop', '1');
      } else {
        u.searchParams.set('autoplay', '1');
        u.searchParams.set('mute', '1');
        u.searchParams.set('playsinline', '1');
      }
      return u.toString();
    } catch (e) {
      return null;
    }
  };
  const registerIframe = (iframe) => {
    if (registered.has(iframe)) return;
    const src = iframe.getAttribute('src') || iframe.dataset.src || '';
    if (!EMBED.test(src) || skipped(iframe)) return;
    if (iframe.closest('video-media[host]')) return; /* handled through the Prestige player below */
    registered.add(iframe);
    iframe.dataset.autoplayState = 'idle';
    const start = () => {
      if (iframe.dataset.autoplayState !== 'idle') return;
      const next = autoplaySrc(iframe.getAttribute('src') || iframe.dataset.src || '');
      if (!next) return;
      iframe.dataset.autoplayState = 'playing';
      const allow = iframe.getAttribute('allow') || '';
      if (!/autoplay/.test(allow)) iframe.setAttribute('allow', (allow ? allow + '; ' : '') + 'autoplay; encrypted-media');
      iframe.setAttribute('src', next);
    };
    if (observer) {
      const once = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            once.disconnect();
            start();
          }
        });
      }, { rootMargin: '0px', threshold: 0.25 });
      once.observe(iframe);
    } else start();
  };

  /* ---------- click-to-load players ---------- */
  /* Dawn <deferred-media>: insert its template content (without Dawn's pauseAllMedia) when it comes into view. */
  const loadDeferred = (el) => {
    if (el.getAttribute('loaded')) return;
    const template = el.querySelector('template');
    if (!template || !template.content || !template.content.firstElementChild) return;
    const holder = document.createElement('div');
    holder.appendChild(template.content.firstElementChild.cloneNode(true));
    const media = holder.querySelector('video, iframe');
    if (!media) return; /* 3D models etc. stay click-to-load */
    el.setAttribute('loaded', true);
    el.appendChild(media);
    scan(el);
  };
  /* Prestige <video-media>: use the theme's own player so its poster / play button stay in sync.
     Marking it "autoplay" makes the theme mute YouTube / Vimeo; uploaded videos are muted here.
     If the browser blocks playback, the theme itself shows the video controls. */
  const prestigeIn = (el) => {
    if (!el.hasAttribute('autoplay')) el.setAttribute('autoplay', '');
    const video = el.querySelector('video');
    if (video) prepare(video);
    if (typeof el.play === 'function') {
      try { el.play(); } catch (e) {}
    }
  };
  const prestigeOut = (el) => {
    if (typeof el.pause === 'function') {
      try { el.pause(); } catch (e) {}
    }
  };
  const registerPlayer = (el) => {
    if (registered.has(el) || skipped(el)) return;
    registered.add(el);
    if (el.tagName === 'DEFERRED-MEDIA') {
      if (!observer) { loadDeferred(el); return; }
      const once = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            once.disconnect();
            loadDeferred(el);
          }
        });
      }, { rootMargin: '0px', threshold: 0.25 });
      once.observe(el);
      return;
    }
    if (!observer) { prestigeIn(el); return; }
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.isIntersecting ? prestigeIn(el) : prestigeOut(el)));
    }, { rootMargin: '0px', threshold: 0.25 }).observe(el);
  };

  const scan = (root) => {
    root = root || document;
    const all = (selector) => {
      const list = root.querySelectorAll ? Array.from(root.querySelectorAll(selector)) : [];
      if (root.matches && root.matches(selector)) list.unshift(root);
      return list;
    };
    all('video').forEach(registerVideo);
    all('iframe').forEach(registerIframe);
    all('deferred-media, video-media').forEach(registerPlayer);
  };

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        visible.set(video, entry.isIntersecting);
        if (entry.isIntersecting) {
          tryPlay(video);
        } else {
          userPaused.delete(video); /* scrolling away resets a manual pause */
          if (!video.paused) {
            ourPause.add(video);
            video.pause();
            video.dataset.autoplayState = 'paused';
          }
        }
      });
    }, { rootMargin: '0px', threshold: 0.25 });
  }

  window.SiteVideoAutoplay = { scan };
  const start = () => {
    scan(document);
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) scan(node);
      }));
    }).observe(document.documentElement, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    document.querySelectorAll('video[data-autoplay-state]').forEach((video) => tryPlay(video));
  });
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
